import {
    addSaleApi,
    checkoutListingApi,
    removeListingFromCart,
    sendFreePurchaseReceivedEmailApi,
    updateSalesApi
} from '../../util/api';
import { storableError } from '../../util/errors';
import { USER } from '../../util/fetchData';
import { denormalisedResponseEntities } from '../../util/data';
import { createImageVariantConfig } from '../../util/sdkLoader';
import * as log from '../../util/log';
import { fetchCartData } from '../../ducks/cart.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';

const LISTING_CART = process.env.REACT_APP_LISTING_FOR_CHECKOUT;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CartPage/SET_INITIAL_VALUES';

export const REMOVE_CART_ITEM_REQUEST = 'app/CartPage/REMOVE_CART_ITEM_REQUEST';
export const REMOVE_CART_ITEM_SUCCESS = 'app/CartPage/REMOVE_CART_ITEM_SUCCESS';
export const REMOVE_CART_ITEM_ERROR = 'app/CartPage/REMOVE_CART_ITEM_ERROR';

export const CHECK_CART_REQUEST = 'app/CartPage/CHECK_CART_REQUEST';
export const CHECK_CART_SUCCESS = 'app/CartPage/CHECK_CART_SUCCESS';
export const CHECK_CART_ERROR = 'app/CartPage/CHECK_CART_ERROR';

export const FETCH_RECENTLY_VIEWED_LISTINGS_REQUEST = 'app/CartPage/FETCH_RECENTLY_VIEWED_LISTINGS_REQUEST';
export const FETCH_RECENTLY_VIEWED_LISTINGS_SUCCESS = 'app/CartPage/FETCH_RECENTLY_VIEWED_LISTINGS_SUCCESS';
export const FETCH_RECENTLY_VIEWED_LISTINGS_ERROR = 'app/CartPage/FETCH_RECENTLY_VIEWED_LISTINGS_ERROR';

export const GET_CART_REQUEST = 'app/CartPage/GET_CART_REQUEST';
export const GET_CART_SUCCESS = 'app/CartPage/GET_CART_SUCCESS';
export const GET_CART_ERROR = 'app/CartPage/GET_CART_ERROR';

export const CHECKOUT_FREE_CART_REQUEST = 'app/CartPage/CHECKOUT_FREE_CART_REQUEST';
export const CHECKOUT_FREE_CART_SUCCESS = 'app/CartPage/CHECKOUT_FREE_CART_SUCCESS';
export const CHECKOUT_FREE_CART_ERROR = 'app/CartPage/CHECKOUT_FREE_CART_ERROR';

// ================ Reducer ================ //
const initialState = {
    cart: null,
    getCartInProgress: false,
    getCartError: null,

    recentlyViewedListings: [],
    fetchRecentlyViewedListingsInProgress: false,
    fetchRecentlyViewedListingsError: null,

    fetchCartDataInProgress: false,
    fetchCartDataError: null,

    removeCartItemInProgress: false,
    removeCartItemError: null,

    checkoutFreeCartInProgress: false,
    checkoutFreeCartError: null,
};

const cartPageReducer = (state = initialState, action = {}) => {
    const { type, payload } = action;
    switch (type) {
        case SET_INITIAL_VALUES:
            return { ...initialState, ...payload };

        case CHECK_CART_REQUEST:
            return {
                ...state,
                cartId: null,
            };
        case CHECK_CART_SUCCESS:
            return {
                ...state,
                cartId: payload.cartId,
            };
        case CHECK_CART_ERROR:
            return {
                ...state,
                cartId: null,
            };

        case REMOVE_CART_ITEM_REQUEST:
            return {
                ...state,
                removeCartItemInProgress: true,
                removeCartItemError: null,
            };
        case REMOVE_CART_ITEM_SUCCESS:
            return {
                ...state,
                removeCartItemInProgress: false,
            };
        case REMOVE_CART_ITEM_ERROR:
            return {
                ...state,
                removeCartItemInProgress: false,
                removeCartItemError: payload,
            };

        case FETCH_RECENTLY_VIEWED_LISTINGS_REQUEST:
            return {
                ...state,
                fetchRecentlyViewedListingsInProgress: true,
                fetchRecentlyViewedListingsError: null,
                recentlyViewedListings: [],
            };
        case FETCH_RECENTLY_VIEWED_LISTINGS_SUCCESS:
            return {
                ...state,
                fetchRecentlyViewedListingsInProgress: false,
                recentlyViewedListings: payload,
            };
        case FETCH_RECENTLY_VIEWED_LISTINGS_ERROR:
            return {
                ...state,
                fetchRecentlyViewedListingsInProgress: false,
                fetchRecentlyViewedListingsError: payload,
            };

        case GET_CART_REQUEST:
            return {
                ...state,
                getCartInProgress: true,
                getCartError: null,
            };
        case GET_CART_SUCCESS:
            return {
                ...state,
                getCartInProgress: false,
                cart: payload,
            };
        case GET_CART_ERROR:
            return {
                ...state,
                getCartInProgress: false,
                getCartError: payload,
            };

        default:
            return state;
    }
}

export default cartPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
    type: SET_INITIAL_VALUES,
    payload: initialValues,
});

export const removeCartItemRequest = () => ({
    type: REMOVE_CART_ITEM_REQUEST,
});

export const removeCartItemSuccess = cartData => ({
    type: REMOVE_CART_ITEM_SUCCESS,
});

export const removeCartItemError = e => ({
    type: REMOVE_CART_ITEM_ERROR,
    error: true,
    payload: e,
});

export const checkCartRequest = () => ({
    type: CHECK_CART_REQUEST,
});

export const checkCartSuccess = cart => ({
    type: CHECK_CART_SUCCESS,
    payload: cart,
});

export const checkCartError = e => ({
    type: CHECK_CART_ERROR,
    error: true,
    payload: e,
});

export const fetchRecentlyViewedListingsRequest = () => ({
    type: FETCH_RECENTLY_VIEWED_LISTINGS_REQUEST,
});

export const fetchRecentlyViewedListingsSuccess = listings => ({
    type: FETCH_RECENTLY_VIEWED_LISTINGS_SUCCESS,
    payload: listings,
});

export const fetchRecentlyViewedListingsError = e => ({
    type: FETCH_RECENTLY_VIEWED_LISTINGS_ERROR,
    error: true,
    payload: e,
});

export const getCartRequest = () => ({
    type: GET_CART_REQUEST,
});

export const getCartSuccess = cart => ({
    type: GET_CART_SUCCESS,
    payload: cart,
});

export const getCartError = e => ({
    type: GET_CART_ERROR,
    error: true,
    payload: e,
});

export const checkoutFreeCartRequest = () => ({
    type: CHECKOUT_FREE_CART_REQUEST,
});
export const checkoutFreeCartSuccess = () => ({
    type: CHECKOUT_FREE_CART_SUCCESS,
});
export const checkoutFreeCartError = e => ({
    type: CHECKOUT_FREE_CART_ERROR,
    error: true,
    payload: e,
});

/* ================ Thunks ================ */

export const removeCartItem = (listingId) => (dispatch, getState, sdk) => {
    dispatch(removeCartItemRequest());
    return removeListingFromCart(listingId)
        .then(async () => {
            await Promise.all([
                dispatch(removeCartItemSuccess()),
                dispatch(fetchCartData()),
            ]);
        })
        .catch(e => {
            dispatch(removeCartItemError(storableError(e)));
            log.error(e, 'remove-cart-item-failed', {
                listingId,
            });
        });
}

export const fetchRecentlyViewedListings = (config) => async (dispatch, getState, sdk) => {
    dispatch(fetchRecentlyViewedListingsRequest());
    const { currentUser } = getState().user;
    const { recentlyViewedListings } = USER(currentUser).getPublicData();
    if (!recentlyViewedListings || !recentlyViewedListings.length) {
        dispatch(fetchRecentlyViewedListingsSuccess([]));
        return;
    }

    try {
        const {
            aspectWidth = 1,
            aspectHeight = 1,
            variantPrefix = 'listing-card',
        } = config.layout.listingImage;
        const aspectRatio = aspectHeight / aspectWidth;

        const listingParams = {
            include: ['author', 'images'],
            'fields.image': [
                `variants.${variantPrefix}`,
            ],
            ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        };

        const listings = await Promise.all(recentlyViewedListings.map((listingId) => {
            return sdk.listings.show({
                id: listingId,
                ...listingParams,
            }).then(response => denormalisedResponseEntities(response)[0]);
        }))
        dispatch(fetchRecentlyViewedListingsSuccess(listings));

    } catch (error) {
        dispatch(fetchRecentlyViewedListingsError(error));
        log.error(error, 'fetching-recently-viewed-listings', {
            currentUser,
        });
    }
}

const getListingCart = () => (dispatch, getState, sdk) => {
    dispatch(getCartRequest());
    return sdk.listings.show({
        id: LISTING_CART,
        include: ['author'],
    }).then(response => {
        const cart = denormalisedResponseEntities(response)[0];
        dispatch(getCartSuccess(cart));
        return cart;
    }).catch(e => {
        dispatch(getCartError(storableError(e)));
        log.error(e, 'get-cart-failed', {});
    })
}

export const checkoutFreeCart = (listingId) => (dispatch, getState, sdk) => {
    dispatch(checkoutFreeCartRequest());
    return checkoutListingApi({ listingId })
        .then(response => {
            dispatch(checkoutFreeCartSuccess());
            return response.data.orderId;
        }).then(async (orderId) => {
            await updateSalesApi({ transactionId: orderId?.uuid });
            await addSaleApi({ transactionId: orderId?.uuid });
            return orderId;
        })
        .catch(e => {
            dispatch(checkoutFreeCartError(storableError(e)));
            log.error(e, 'checkout-free-cart-failed', {
                listingId,
            });
        });
}

export const loadData = (params, search, config) => async dispatch => {
    return dispatch(fetchCurrentUser()).then(_ => {
        dispatch(fetchCartData(true))
        dispatch(fetchRecentlyViewedListings(config))
        dispatch(getListingCart())
    })
}
